<script setup lang="ts">
  import { inject, useCssModule } from 'vue';

  import { RADIO_GROUP_INJECTION } from '../RadioGroup.keys';

  const radioGroupInjection = inject(RADIO_GROUP_INJECTION.key);

  if (!radioGroupInjection) {
    throw new Error('VariantButton must be used with a RadioGroup instance.');
  }

  const { name, disabled, fullWidth, modelValue, options, update } = radioGroupInjection;
  const classes = useCssModule();
</script>

<template>
  <div class="flex" :class="classes.root">
    <div v-for="option in options" :key="`${name}-${option.id}`" :class="[{ 'w-full': fullWidth }]">
      <input
        :id="`${name}-${option.id}`"
        class="sr-only"
        type="radio"
        :name="name"
        :value="option.value"
        :checked="modelValue === option.value"
        :disabled="disabled || option.disabled"
        @input="update"
      />
      <label :for="`${name}-${option.id}`">
        {{ option.text }}
      </label>
    </div>
  </div>
</template>

<style module>
  .root label {
    padding: 8px 30px;
    border: 1px solid var(--color-ice-500);
    font-weight: var(--stash-font-weight-semibold);
    color: var(--color-ice-700);
    cursor: pointer;
    transition: all 0.2s;
    user-select: none;
    display: block;
    text-align: center;
  }

  .root > div:first-child label {
    border-top-left-radius: var(--radius-sm);
    border-bottom-left-radius: var(--radius-sm);
  }

  .root > div:last-child label {
    border-top-right-radius: var(--radius-sm);
    border-bottom-right-radius: var(--radius-sm);
  }

  .root > div:not(:first-child) label {
    margin-left: -1px;
  }

  .root > div:not(:last-child) label {
    border-right-color: transparent;
  }

  .root input:disabled ~ label {
    background-color: var(--color-ice-200);
    color: var(--color-ice-500);
    cursor: auto;
  }

  .root input:not(:checked, :disabled) ~ label:hover {
    border-color: var(--color-blue-500);
    color: var(--color-blue-500);
    z-index: 1;
    position: relative;
  }

  .root input:checked:not(:disabled) ~ label {
    border-color: var(--color-blue-500);
    color: var(--color-blue-500);
    background-color: var(--color-blue-100);
    z-index: 1;
    position: relative;
  }
</style>
